import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Inject,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ValidationError } from 'validation';
import { ValidationService } from './../validation/validation.service';
import { Control } from './../control/control';
import { VirtualKeyboardService } from '../virtual-keyboard/virtual-keyboard.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';

@Component({
  selector: 'i-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent extends Control implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() inpPath: string;
  @Input() inpPathForValidation: string;
  @Input() inpModel: string | null;
  @Input() inpClass: string = '';
  @Input() inpLabel: string;
  @Input() inpTabIndex: number = 0;
  @Input() inpUpper: boolean = false;
  @Input() inpMultiline: boolean = false;
  @Input() inpDisabled: boolean = false;
  @Input() inpOptions: string[] = [];
  @Input() inpPassword: boolean = false;
  @Input() inpMinWidth: number = 250;
  @Input() inpKeyboardLayoutLanguage: 'ru' | 'en' | null = null;
  @Output() inpModelChange = new EventEmitter<string | null>();

  model: string | null = null;
  transformStyle: string = '';
  keyboardInsideScroll: string = '';
  readonly appearance?: MatFormFieldDefaultOptions['appearance'];

  private validationIndex: number;
  private dirty: boolean = false;
  private lastFocusMoment: Date = new Date();
  private autocompleteFiredByClick: boolean = false;
  private dead = new ReplaySubject<void>();

  @ViewChild('self') private self: ElementRef<HTMLInputElement>;
  @ViewChild('self', { read: MatAutocompleteTrigger }) autocomplete: MatAutocompleteTrigger;

  constructor(
    private cdr: ChangeDetectorRef,
    private validationService: ValidationService,
    private virtualKeyboardService: VirtualKeyboardService,
    @Inject(MAT_FORM_FIELD_DEFAULT_OPTIONS) private options: MatFormFieldDefaultOptions,
  ) {
    super();
    this.appearance = this.options.appearance;
  }

  ngOnInit() {
    if (this.inpUpper) {
      this.transformStyle = 'uppercase';
    }
    if (this.virtualKeyboardService.getInsideScroll()) {
      this.keyboardInsideScroll = this.virtualKeyboardService.getHeight() + 'px';
    }
    const validationRegistration = this.validationService.register({
      path: this.inpPathForValidation,
      pathExact: true,
      consumer: (final: boolean, errors: ValidationError[]) => {
        if (final || this.dirty) {
          this.decorate(errors);
          this.showCurrentTooltip();
          this.cdr.markForCheck();
        }
      },
      getElementForFocus: () => this.getElementForFocus(),
    });
    this.validationIndex = validationRegistration.regKey;
    this.dirty = validationRegistration.dirty;
  }

  ngAfterViewInit(): void {
    this.autocomplete &&
      this.autocomplete.autocomplete.optionSelected.pipe(takeUntil(this.dead)).subscribe((r) => {
        this.checkAutocompleteFiredByClick();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    let inpModelChange = changes['inpModel'];
    if (inpModelChange) {
      if (this.inpUpper && this.model !== null && this.model.toUpperCase() === inpModelChange.currentValue) {
        // Do not update control (otherwise caret position could be lost)
      } else {
        this.model = inpModelChange.currentValue;
      }
      if (!inpModelChange.isFirstChange()) {
        this.dirty = true;
        this.validationService.validate();
      }
    }
  }

  ngOnDestroy() {
    this.validationService.unregister(this.validationIndex, this.dirty);
    this.dead.next();
    this.dead.complete();
  }

  focused(self: HTMLInputElement | HTMLTextAreaElement, tooltip: MatTooltip) {
    this.lastFocusMoment = new Date();
    this.showTooltip(tooltip);
    this.virtualKeyboardService.focus(
      self,
      (val: string) => {
        this.model = val;
        this.changed();
        if (this.autocomplete) {
          this.autocomplete.openPanel();
        }
      },
      (dir: boolean) => {
        this.validationService.navigate(self, dir);
      },
      () => {
        if (document.activeElement === self) {
          self.scrollIntoView({ block: 'center' });
          setTimeout(() => {
            if (document.activeElement === self) {
              if (this.autocomplete) {
                if (this.autocompleteFiredByClick) {
                  this.autocompleteFiredByClick = false;
                } else {
                  this.autocomplete.openPanel();
                }
              }
            }
          }, 1);
        }
      },
      this.inpKeyboardLayoutLanguage,
    );
  }

  blurred() {
    this.hideTooltip();
    this.virtualKeyboardService.blur();
  }

  changed() {
    this.inpModelChange.emit(this.inpUpper ? (this.model === null ? null : this.model.toUpperCase()) : this.model);
  }

  private getElementForFocus(): HTMLElement | null {
    return this.inpDisabled ? null : this.self.nativeElement;
  }

  focus() {
    const element = this.getElementForFocus();
    if (element) {
      element.focus();
    }
  }

  private checkAutocompleteFiredByClick() {
    const x = new Date();
    if (x.getTime() - this.lastFocusMoment.getTime() < 50) {
      this.autocompleteFiredByClick = true;
    }
  }
}
